import SaveIcon from '@mui/icons-material/Save';
import { LoadingButton } from '@mui/lab';
import Alert from '@mui/material/Alert';
import {
  Box,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import { SectionCard } from '../../../components/Section';
import {
  ALPHA_3_COUNTRIES,
  TREASURY_KYB_DEFAULT_FUNDING_SOURCES,
  TREASURY_KYB_DEFAULT_INDUSTRY,
  TREASURY_KYB_DEFAULT_LEGAL_ENTITY_TYPE,
  TREASURY_KYB_FUNDING_SOURCES,
  TREASURY_KYB_INDUSTRIES,
  TREASURY_KYB_LEGAL_ENTITY_TYPES,
  TREASURY_KYB_OWNER_PERCENTAGE,
} from '../../../constants/treasury';
import { treasuryKybFormFields } from '../../../forms/fields/formFields/treasuryKyb';
import { treasuryKybSchema } from '../../../forms/validationSchema/formSchema/treasuryKyb';
import { usePermissions } from '../../../hooks';
import { useSaveTreasuryKybMutation } from '../../../redux/features/treasury/treasury.apiSlice';
import { TreasuryKyb } from '../../../types/treasury';
import * as permissions from '../../../utils/permissions';
import { getTreasuryErrorMessage, toDateOnly } from '../../../utils/treasury';
import { AddressesTable } from '../../User/components/Kyc/AddressesTable';

type TreasuryKybStepProps = {
  companyId: number;
  kyb: TreasuryKyb | null;
  /** The owner's madkhol-user id — their primary address is the one Alpaca gets as the owner's residential address. */
  ownerUserId?: number | null;
  onDone: () => void;
};

const F = treasuryKybFormFields;

const AMOUNT_FIELDS: { min: treasuryKybFormFields; max: treasuryKybFormFields; label: string }[] = [
  { min: F.annualIncomeMin, max: F.annualIncomeMax, label: 'Annual Income' },
  { min: F.liquidNetWorthMin, max: F.liquidNetWorthMax, label: 'Liquid Net Worth' },
  { min: F.totalNetWorthMin, max: F.totalNetWorthMax, label: 'Total Net Worth' },
];

/**
 * Step 5 — the company KYB for its Alpaca account. Saving again replaces it (and clears an admin verification) until it
 * has gone to Alpaca; after that the backend refuses (KYB_ALREADY_SUBMITTED). The owner is the treasury employee.
 */
export const TreasuryKybStep = ({ companyId, kyb, ownerUserId, onDone }: TreasuryKybStepProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const canSave = usePermissions({ items: [permissions.Treasury.updateKyb] });
  const canReadAddresses = usePermissions({ items: [permissions.User.readAddress] });
  const [saveTreasuryKyb, { isLoading: isSaving }] = useSaveTreasuryKybMutation();
  const isSubmitted = Boolean(kyb?.submittedAt);

  const { handleSubmit, values, handleChange, setFieldValue, touched, errors, isValid } = useFormik({
    initialValues: {
      [F.legalEntityType]: kyb?.legalEntityType ?? TREASURY_KYB_DEFAULT_LEGAL_ENTITY_TYPE,
      [F.dateOfIncorporation]: kyb?.dateOfIncorporation ?? '',
      [F.industry]: kyb?.industry ?? TREASURY_KYB_DEFAULT_INDUSTRY,
      [F.fundingSource]: kyb?.fundingSource ?? TREASURY_KYB_DEFAULT_FUNDING_SOURCES,
      [F.countryOfIncorporation]: kyb?.countryOfIncorporation ?? 'SAU',
      [F.countryOfTaxResidence]: kyb?.countryOfTaxResidence ?? 'SAU',
      [F.annualIncomeMin]: kyb?.annualIncomeMin ?? '',
      [F.annualIncomeMax]: kyb?.annualIncomeMax ?? '',
      [F.liquidNetWorthMin]: kyb?.liquidNetWorthMin ?? '',
      [F.liquidNetWorthMax]: kyb?.liquidNetWorthMax ?? '',
      [F.totalNetWorthMin]: kyb?.totalNetWorthMin ?? '',
      [F.totalNetWorthMax]: kyb?.totalNetWorthMax ?? '',
      [F.isParticipatingFatca]: kyb?.isParticipatingFatca ?? false,
      [F.isExemptFromTaxUnder501a]: kyb?.isExemptFromTaxUnder501a ?? false,
      [F.ownerPartyEmail]: kyb?.ownerPartyEmail ?? '',
      [F.entityPartyEmail]: kyb?.entityPartyEmail ?? '',
      [F.titleAtCompany]: kyb?.ubo?.titleAtCompany ?? '',
      [F.countryOfBirth]: kyb?.ubo?.countryOfBirth ?? 'SAU',
    },
    validationSchema: treasuryKybSchema,
    validateOnMount: true,
    enableReinitialize: true,
    onSubmit: async (formValues) => {
      try {
        await saveTreasuryKyb({
          companyId,
          payload: {
            legalEntityType: String(formValues[F.legalEntityType]).trim(),
            dateOfIncorporation: String(formValues[F.dateOfIncorporation]),
            industry: String(formValues[F.industry]).trim(),
            fundingSource: formValues[F.fundingSource] as string[],
            countryOfIncorporation: String(formValues[F.countryOfIncorporation]),
            countryOfTaxResidence: String(formValues[F.countryOfTaxResidence]),
            annualIncomeMin: Number(formValues[F.annualIncomeMin]),
            annualIncomeMax: Number(formValues[F.annualIncomeMax]),
            liquidNetWorthMin: Number(formValues[F.liquidNetWorthMin]),
            liquidNetWorthMax: Number(formValues[F.liquidNetWorthMax]),
            totalNetWorthMin: Number(formValues[F.totalNetWorthMin]),
            totalNetWorthMax: Number(formValues[F.totalNetWorthMax]),
            isParticipatingFatca: Boolean(formValues[F.isParticipatingFatca]),
            isExemptFromTaxUnder501a: Boolean(formValues[F.isExemptFromTaxUnder501a]),
            ownerPartyEmail: String(formValues[F.ownerPartyEmail]).trim(),
            entityPartyEmail: String(formValues[F.entityPartyEmail]).trim(),
            ubo: {
              percentageOwnership: TREASURY_KYB_OWNER_PERCENTAGE,
              titleAtCompany: String(formValues[F.titleAtCompany]).trim(),
              countryOfBirth: String(formValues[F.countryOfBirth]),
            },
          },
        }).unwrap();
        enqueueSnackbar('Company KYB saved!', { variant: 'success' });
        onDone();
      } catch (error) {
        enqueueSnackbar(getTreasuryErrorMessage(error, 'Company KYB save error!'), { variant: 'error' });
      }
    },
  });

  const fieldError = (field: treasuryKybFormFields): string => {
    const message = (errors as Record<string, unknown>)[field];

    return (touched as Record<string, boolean>)[field] && typeof message === 'string' ? message : '';
  };

  const optionSelect = (field: treasuryKybFormFields, label: string, options: { value: string; label: string }[]) => (
    <FormControl fullWidth size="small" required error={Boolean(fieldError(field))}>
      <InputLabel id={`${field}-label`}>{label}</InputLabel>
      <Select
        labelId={`${field}-label`}
        id={field}
        name={field}
        value={values[field]}
        label={label}
        onChange={handleChange}
        disabled={isSubmitted}
      >
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
      {fieldError(field) && <FormHelperText>{fieldError(field)}</FormHelperText>}
    </FormControl>
  );

  const countrySelect = (field: treasuryKybFormFields, label: string) => (
    <FormControl fullWidth size="small" required error={Boolean(fieldError(field))}>
      <InputLabel id={`${field}-label`}>{label}</InputLabel>
      <Select
        labelId={`${field}-label`}
        id={field}
        name={field}
        value={values[field]}
        label={label}
        onChange={handleChange}
        disabled={isSubmitted}
      >
        {ALPHA_3_COUNTRIES.map((country) => (
          <MenuItem key={country.value} value={country.value}>
            {country.label}
          </MenuItem>
        ))}
      </Select>
      {fieldError(field) && <FormHelperText>{fieldError(field)}</FormHelperText>}
    </FormControl>
  );

  return (
    <form onSubmit={handleSubmit}>
      <Stack spacing={2}>
        {isSubmitted && <Alert severity="info">This KYB has been sent to Alpaca and can no longer be changed.</Alert>}
        {!isSubmitted && kyb?.verification && (
          <Alert severity="warning">
            Saving again clears the admin verification — it will need to be verified again.
          </Alert>
        )}

        <SectionCard title="Company" description="The legal entity Alpaca opens the account for.">
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              {optionSelect(F.legalEntityType, 'Legal Entity Type', TREASURY_KYB_LEGAL_ENTITY_TYPES)}
            </Grid>
            <Grid item xs={12} md={6}>
              <DatePicker
                label="Date of Incorporation *"
                value={values[F.dateOfIncorporation] ? new Date(`${values[F.dateOfIncorporation]}T00:00:00`) : null}
                onChange={(newValue) => setFieldValue(F.dateOfIncorporation, toDateOnly(newValue as Date | null))}
                disabled={isSubmitted}
                disableFuture
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'small',
                    error: Boolean(fieldError(F.dateOfIncorporation)),
                    helperText: fieldError(F.dateOfIncorporation) || 'The CR issue date, not its expiry',
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              {optionSelect(F.industry, 'Industry', TREASURY_KYB_INDUSTRIES)}
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small" required error={Boolean(fieldError(F.fundingSource))}>
                <InputLabel id={`${F.fundingSource}-label`}>Funding Sources</InputLabel>
                <Select
                  labelId={`${F.fundingSource}-label`}
                  id={F.fundingSource}
                  multiple
                  value={values[F.fundingSource] as string[]}
                  label="Funding Sources"
                  onChange={(event) => {
                    const { value } = event.target;
                    setFieldValue(F.fundingSource, typeof value === 'string' ? value.split(',') : value);
                  }}
                  disabled={isSubmitted}
                  renderValue={(selected) =>
                    (selected as string[])
                      .map(
                        (value) =>
                          TREASURY_KYB_FUNDING_SOURCES.find((option) => option.value === value)?.label ?? value,
                      )
                      .join(', ')
                  }
                >
                  {TREASURY_KYB_FUNDING_SOURCES.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                {fieldError(F.fundingSource) && <FormHelperText>{fieldError(F.fundingSource)}</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              {countrySelect(F.countryOfIncorporation, 'Country of Incorporation')}
            </Grid>
            <Grid item xs={12} md={6}>
              {countrySelect(F.countryOfTaxResidence, 'Country of Tax Residence')}
            </Grid>
          </Grid>
        </SectionCard>

        <SectionCard title="Financial Profile" description="The company's real ranges, in USD — Alpaca needs all six.">
          <Grid container spacing={2}>
            {AMOUNT_FIELDS.map(({ min, max, label }) => (
              <Grid item xs={12} md={4} key={label}>
                <Stack spacing={2}>
                  <Typography variant="subtitle2">{label}</Typography>
                  <TextField
                    fullWidth
                    required
                    size="small"
                    id={min}
                    name={min}
                    label="Min"
                    type="number"
                    value={values[min]}
                    onChange={handleChange}
                    disabled={isSubmitted}
                    error={Boolean(fieldError(min))}
                    helperText={fieldError(min)}
                  />
                  <TextField
                    fullWidth
                    required
                    size="small"
                    id={max}
                    name={max}
                    label="Max"
                    type="number"
                    value={values[max]}
                    onChange={handleChange}
                    disabled={isSubmitted}
                    error={Boolean(fieldError(max))}
                    helperText={fieldError(max)}
                  />
                </Stack>
              </Grid>
            ))}
          </Grid>
        </SectionCard>

        <SectionCard
          title="FATCA"
          description="A US tax law: financial institutions outside the US report accounts held by US persons to the IRS. Alpaca asks every company to declare its status — an ordinary company leaves both off."
        >
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(values[F.isParticipatingFatca])}
                    onChange={(event) => setFieldValue(F.isParticipatingFatca, event.target.checked)}
                    disabled={isSubmitted}
                  />
                }
                label="Participating FFI"
              />
              <FormHelperText>
                On only if the company is a financial institution (bank, fund, broker) that signed an FFI agreement with
                the IRS and has a GIIN. An ordinary company: off.
              </FormHelperText>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(values[F.isExemptFromTaxUnder501a])}
                    onChange={(event) => setFieldValue(F.isExemptFromTaxUnder501a, event.target.checked)}
                    disabled={isSubmitted}
                  />
                }
                label="Exempt from tax under 501(a)"
              />
              <FormHelperText>
                On only if the company is exempt from US federal income tax under US tax code section 501(a) — e.g. a US
                charity or pension trust. An ordinary company: off.
              </FormHelperText>
            </Grid>
          </Grid>
        </SectionCard>

        <SectionCard
          title="Alpaca Party Emails"
          description="Two new emails, different from each other and from the owner's own emails — Alpaca refuses an email already in use."
        >
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                size="small"
                id={F.ownerPartyEmail}
                name={F.ownerPartyEmail}
                label="Owner Party Email"
                type="email"
                value={values[F.ownerPartyEmail]}
                onChange={handleChange}
                disabled={isSubmitted}
                error={Boolean(fieldError(F.ownerPartyEmail))}
                helperText={fieldError(F.ownerPartyEmail)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                size="small"
                id={F.entityPartyEmail}
                name={F.entityPartyEmail}
                label="Company Party Email"
                type="email"
                value={values[F.entityPartyEmail]}
                onChange={handleChange}
                disabled={isSubmitted}
                error={Boolean(fieldError(F.entityPartyEmail))}
                helperText={fieldError(F.entityPartyEmail)}
              />
            </Grid>
          </Grid>
        </SectionCard>

        <SectionCard title="Owner" description="The treasury employee — the one owner (100%) and signer.">
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                size="small"
                id={F.titleAtCompany}
                name={F.titleAtCompany}
                label="Title at Company"
                value={values[F.titleAtCompany]}
                onChange={handleChange}
                disabled={isSubmitted}
                error={Boolean(fieldError(F.titleAtCompany))}
                helperText={fieldError(F.titleAtCompany)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              {countrySelect(F.countryOfBirth, 'Country of Birth')}
            </Grid>
          </Grid>
        </SectionCard>

        {/* The owner's address — the same Addresses table and popup as the user's KYC page (madkhol-user). */}
        <Stack spacing={1}>
          <Typography variant="body2" color="text.secondary">
            Owner address: Alpaca receives the owner&apos;s <b>primary</b> address as their residential address —
            building number, street, district, city and post code. Add one and switch &quot;Is Primary Address&quot; on.
          </Typography>
          {ownerUserId && canReadAddresses ? (
            <AddressesTable userId={ownerUserId} />
          ) : (
            <Alert severity="warning">
              {ownerUserId
                ? "You don't have permission to see the owner's addresses."
                : 'The owner has no Madkhol user yet, so there is no address to add.'}
            </Alert>
          )}
        </Stack>

        {!isSubmitted && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <LoadingButton
              type="submit"
              variant="contained"
              color="primary"
              loading={isSaving}
              startIcon={<SaveIcon />}
              disabled={!isValid || isSaving || !canSave}
            >
              Save KYB
            </LoadingButton>
          </Box>
        )}
      </Stack>
    </form>
  );
};
